import { useNotification } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Descriptions, Modal, Popconfirm, Space, Spin, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";

import { api } from "../api";
import { COPY_TOOLTIPS, DATE_FORMAT, formatKurus } from "../labels";
import type { PayoutIban } from "../types";

export type PayablePayout = { id: string; amountKurus: number; scheduledFor: string };

/**
 * Paying one withdrawal request, from /payouts or a tutor's "Finans" tab (permission
 * `payouts.markPaid`): opening the dialog opens the IBAN (recorded in the audit log); after
 * the transfer the request is marked paid.
 */
export function PayoutPayDialog({
  payout,
  onClose,
  onPaid,
}: {
  payout: PayablePayout | null;
  onClose: () => void;
  onPaid: () => void | Promise<unknown>;
}) {
  const { open } = useNotification();
  const [busy, setBusy] = useState(false);
  const iban = useQuery({
    queryKey: ["office", "payout-iban", payout?.id],
    // POST: each opening is an audited action, never cached by the browser.
    queryFn: () => api<PayoutIban>(`/api/office/payouts/${payout!.id}/iban`, { method: "POST" }),
    enabled: payout !== null,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  async function markPaid() {
    if (!payout) return;
    setBusy(true);
    try {
      await api(`/api/office/payouts/${payout.id}/paid`, { method: "POST" });
      open?.({ type: "success", message: "Ödeme talebi ödendi olarak işaretlendi." });
      await onPaid();
      onClose();
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Wise gönderim bilgileri"
      open={payout !== null}
      onCancel={onClose}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={onClose}>Kapat</Button>
          <Popconfirm
            title="Wise ile gönderildi mi?"
            description="Talep ödendi olarak işaretlenir; geri alınamaz."
            okText="Ödendi"
            cancelText="Vazgeç"
            onConfirm={markPaid}
            disabled={!iban.data}
          >
            <Button type="primary" loading={busy} disabled={!iban.data}>
              Ödendi olarak işaretle
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      {iban.isPending ? <Spin /> : null}
      {iban.error ? <Alert type="error" showIcon message={iban.error.message} /> : null}
      {payout && iban.data ? (
        <>
          {iban.data.changedSinceRequest ? (
            <Alert
              type="warning"
              showIcon
              message="Eğitmen IBAN'ını talepten sonra değiştirmiş. Göndermeden önce eğitmenle teyit et."
              style={{ marginBottom: 16 }}
            />
          ) : null}
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Alıcı">{iban.data.accountHolder}</Descriptions.Item>
            <Descriptions.Item label="Banka">{iban.data.bankName ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="SWIFT">
              {iban.data.swiftCode ? (
                <Typography.Text
                  copyable={{ text: iban.data.swiftCode, tooltips: COPY_TOOLTIPS }}
                  code
                >
                  {iban.data.swiftCode}
                </Typography.Text>
              ) : (
                "— (eğitmen henüz girmedi)"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="IBAN">
              <Typography.Text
                copyable={{ text: iban.data.iban.replace(/\s/g, ""), tooltips: COPY_TOOLTIPS }}
                code
                data-testid="payout-iban"
              >
                {iban.data.iban}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Tutar">{formatKurus(payout.amountKurus)}</Descriptions.Item>
            <Descriptions.Item label="Ödeme günü">
              {dayjs(payout.scheduledFor).format(DATE_FORMAT)}
            </Descriptions.Item>
          </Descriptions>
          <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
            IBAN'ı görüntülemen İşlem kayıtlarına yazıldı.
          </Typography.Paragraph>
        </>
      ) : null}
    </Modal>
  );
}
